// components/submissions/RightSubmissionPanel.tsx
import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  File,
  getStudentSubmissionDetails,
  ProjectSubmissionsResponse,
} from "../../api/studentSubmissionsApi";
import { Card, CardHeader, CardContent } from "../ui/card";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../ui/accordion";
import { ScrollArea } from "../ui/scroll-area";
import { FileText, User, Clock, Download, BarChart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FileItem } from "../SubmitAssignmentDialog";

type Props = {
  members?: { userId: string; name: string }[] | null;
  projectId: string;
  maxAttempts?: number;
};

interface SubmissionDetail {
  submissionId: string;
  checkPointId: string;
  checkPointName: string;
  attempts: number;
  instructor_feedback: string;
  files: {
    name: string;
    size: string;
    url?: string;
  }[];
  submissionDate: string;
  studentComments?: string;
  gradeBreakdown: {
    criteriaName: string;
    earnedPoints: number;
    totalPoints: number;
  }[];
}

function safeFormatDate(input?: string) {
  if (!input) return "—";
  const d = new Date(input);
  if (!isNaN(d.getTime())) {
    return d.toLocaleString();
  }
  return input;
}

function sumPoints(rows: SubmissionDetail["gradeBreakdown"]) {
  return rows.reduce(
    (acc, r) => {
      acc.earned += Number(r.earnedPoints ?? 0);
      acc.total += Number(r.totalPoints ?? 0);
      return acc;
    },
    { earned: 0, total: 0 },
  );
}

// Map API response to details format
function mapApiResponseToDetails(
  apiResponse: ProjectSubmissionsResponse,
): SubmissionDetail[] {
  const submission = apiResponse?.data?.submission;

  if (!submission || !submission.checkpoints) {
    return [];
  }

  const mainSubmission = submission.submission;

  return submission.checkpoints.map((checkpoint) => {
    const files: SubmissionDetail["files"] = [];

    // Add files from checkpoint progress
    if (
      checkpoint.progress?.filesSubmitted &&
      checkpoint.progress.filesSubmitted.length > 0
    ) {
      checkpoint.progress.filesSubmitted.forEach((fileItem: File) => {
        // Extract the presigned URL - it's nested inside presignedUrl object
        const url =
          typeof fileItem.presignedUrl === "object"
            ? fileItem.presignedUrl?.presignedUrl
            : fileItem.presignedUrl;

        files.push({
          name: fileItem.fileName || "Unknown file",
          size: "N/A",
          url: url || "",
        });
      });
    }

    // Add notebook files from top-level presigned URLs (empty in this case)
    if (
      submission.notebookPresignedUrls &&
      submission.notebookPresignedUrls.length > 0
    ) {
      submission.notebookPresignedUrls.forEach((urlItem, idx) => {
        if (typeof urlItem === "string") {
          const urlString = urlItem;
          const fileName = `notebook_${idx + 1}.ipynb`;
          files.push({
            name: fileName,
            size: "N/A",
            url: urlString,
          });
        } else {
          const obj = urlItem as {
            fileName?: string;
            presignedUrl?: string | { presignedUrl?: string };
          };
          const urlString =
            typeof obj.presignedUrl === "string"
              ? obj.presignedUrl
              : obj.presignedUrl?.presignedUrl || "";
          const fileName = obj.fileName || `notebook_${idx + 1}.ipynb`;
          files.push({
            name: fileName,
            size: "N/A",
            url: urlString,
          });
        }
      });
    }

    // Add other files from top-level presigned URLs (empty in this case)
    if (
      submission.filePresignedUrls &&
      submission.filePresignedUrls.length > 0
    ) {
      submission.filePresignedUrls.forEach((urlItem, idx) => {
        if (typeof urlItem === "string") {
          const urlString = urlItem;
          const fileName = (() => {
            const urlParts = urlString.split("/");
            return urlParts[urlParts.length - 1] || `file_${idx + 1}`;
          })();
          files.push({
            name: fileName,
            size: "N/A",
            url: urlString,
          });
        } else {
          const obj = urlItem as {
            fileName?: string;
            presignedUrl?: string | { presignedUrl?: string };
          };
          const urlString =
            typeof obj.presignedUrl === "string"
              ? obj.presignedUrl
              : obj.presignedUrl?.presignedUrl || "";
          const fileName =
            obj.fileName ||
            (() => {
              const urlParts = urlString.split("/");
              return urlParts[urlParts.length - 1] || `file_${idx + 1}`;
            })();
          files.push({
            name: fileName,
            size: "N/A",
            url: urlString,
          });
        }
      });
    }

    // Extract checkpoint-specific grade breakdown if available
    const gradeBreakdown: SubmissionDetail["gradeBreakdown"] = [];

    if (mainSubmission.grade?.rubricScores && mainSubmission.grade.rubricScores[checkpoint.id]) {
      const rubric = mainSubmission.grade.rubricScores[checkpoint.id];
      
      // Add detailed rubric scores if available
      if (rubric.rubrics && Array.isArray(rubric.rubrics)) {
        rubric.rubrics.forEach((r: any) => {
          if (r.criteriaScores && Array.isArray(r.criteriaScores)) {
            r.criteriaScores.forEach((cs: any) => {
              gradeBreakdown.push({
                criteriaName: cs.criterionName || "Criterion",
                earnedPoints: parseFloat(cs.score || "0"),
                totalPoints: parseFloat(cs.maxPoints || "0"),
              });
            });
          }
        });
      }

      // If no detailed breakdown, at least show the total for the checkpoint
      if (gradeBreakdown.length === 0 && (rubric.checkpointTotalScore !== undefined || rubric.totalScore !== undefined)) {
        gradeBreakdown.push({
          criteriaName: "Checkpoint Total",
          earnedPoints: parseFloat(rubric.checkpointTotalScore || rubric.totalScore || "0"),
          totalPoints: parseFloat(rubric.checkpointMaxScore || rubric.maxScore || "0"),
        });
      }
    }

    return {
      submissionId: mainSubmission.id,
      checkPointId: checkpoint.id,
      checkPointName: checkpoint.title,
      attempts: checkpoint.progress?.attempts || 0,
      // FIX: Access instructor_feedback from checkpoint
      instructor_feedback:
        checkpoint.instructor_feedback ||
        mainSubmission.grade?.rubricScores?.[checkpoint.id]?.checkpointFeedback ||
        "",
      files: files,
      submissionDate: checkpoint.progress?.submittedAt || "",
      studentComments:
        checkpoint.progress?.studentNotes ||
        mainSubmission.metadata?.comments ||
        "",
      gradeBreakdown: gradeBreakdown,
    };
  });
}

export default function RightSubmissionPanel({
  members,
  projectId,
  maxAttempts,
}: Props) {
  const getFileIcon = (type: string) => {
    switch (type) {
      case "pdf":
        return <FileText className="h-4 w-4" />;
      case "ipynb":
        return <BarChart className="h-4 w-4 text-bits-green" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const handleDownload = async (file: {
    name: string;
    size: string;
    url?: string;
  }) => {
    try {
      if (file.url) {
        const response = await fetch(file.url);
        if (!response.ok) throw new Error("Download failed");

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

        toast.success(`${file.name} downloaded successfully`);
      } else {
        toast.info(
          `Download functionality will be available when connected to the backend`,
        );
      }
    } catch (error) {
      toast.error(`Failed to download ${file.name}`);
    }
  };

  // Fetch submission details using useQuery
  const userId = members?.[0]?.userId;

  const {
    data: submissionResponse,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ["studentSubmissionDetails", projectId, userId],
    queryFn: () => getStudentSubmissionDetails(projectId, userId!),
    enabled: !!projectId && !!userId,
    retry: false,
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache the data (formerly cacheTime in older versions)
  });

  // Map API response to details format - ensure we create a fresh array each time
  const details = React.useMemo(() => {
    return submissionResponse
      ? mapApiResponseToDetails(submissionResponse)
      : [];
  }, [submissionResponse]);

  const error = queryError ? "Failed to load submission details" : null;

  if (!members || members.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 p-10">
        <FileText className="h-6 w-6 text-bits-grey-600" />
        <span className="text-md font-normal text-bits-grey-600">
          Select a submission to view details
        </span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-4 p-10">
        <div className="text-sm text-bits-grey-600">
          Loading submission details…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-4 p-10">
        <div className="text-sm text-destructive">{error}</div>
      </div>
    );
  }

  if (!details.length) {
    return (
      <div className="flex flex-col items-center gap-4 p-10">
        <div className="text-sm text-bits-grey-600">
          No submissions found for this student.
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ScrollArea className="h-full">
        <div className="p-4">
          <div className="flex flex-col justify-start">
            <div className="px-4 pt-4 pb-2">
              <div className="text-lg font-medium text-bits-grey">
                Submissions Details
              </div>
              <div className="text-md font-medium p-1">Name</div>
              {members.map((item, index) => (
                <div key={index}>
                  <div className="flex flex-row mb-2">
                    <User className="h-5 w-5 text-bits-grey-600 mr-2" />
                    <div className="text-sm font-normal text-bits-grey-600">
                      {item.name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {submissionResponse?.data?.submission?.submission?.status === "ai_grading" && (
            <div className="px-4 pb-3">
              <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 text-purple-700 rounded-md px-3 py-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                <span className="font-medium">AI Evaluating...</span>
                <span className="text-purple-500 font-normal">Grades will appear once evaluation completes.</span>
              </div>
            </div>
          )}

          {submissionResponse?.data?.submission?.submission?.status === "ai_grading_failed" && (
            <div className="px-4 pb-3">
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">
                <span className="font-medium">AI Evaluation Failed</span>
                <span className="text-red-500 font-normal">The automated grading system encountered an error.</span>
              </div>
            </div>
          )}

          {submissionResponse?.data?.submission?.submission?.grade && (
            <div className="px-4 pb-4">
              <div className="text-md font-medium text-bits-grey mb-3">
                Overall Details
              </div>
              <Card className="p-4 space-y-4">
                <div>
                  <div className="text-sm font-medium text-bits-grey mb-2">
                    Overall Score
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-normal text-bits-grey-600">
                      Total Points
                    </span>
                    <span className="text-xs font-semibold text-bits-grey">
                      {parseFloat(submissionResponse.data.submission.submission.grade.totalScore)}/
                      {parseFloat(submissionResponse.data.submission.submission.grade.maxScore)}
                    </span>
                  </div>
                </div>
                {submissionResponse.data.submission.submission.grade.feedback && (
                  <div>
                    <div className="text-sm font-medium text-bits-grey mb-1">
                      Overall Feedback
                    </div>
                    <p className="bg-bits-blue-25 w-full text-sm font-normal text-bits-grey-600 p-3 rounded-md overflow-y-auto max-h-40 whitespace-pre-wrap">
                      {submissionResponse.data.submission.submission.grade.feedback}
                    </p>
                  </div>
                )}
              </Card>
            </div>
          )}

          <div className="px-2 space-y-3">
            {details.map((sub) => {
              const totals = sumPoints(sub.gradeBreakdown);
              const pct = totals.total
                ? Math.round((totals.earned / totals.total) * 100)
                : 0;

              return (
                <Card
                  key={`${sub.submissionId}-${sub.checkPointId}`}
                  className="w-full gap-1"
                >
                  <CardHeader>
                    <div className="text-md font-medium">
                      Submitted Date & Time
                    </div>
                    <div className="flex flex-row">
                      <div className="flex flex-row mb-2">
                        <Clock className="h-5 w-5 text-bits-grey-600 mr-2" />
                        <div className="text-sm font-normal text-bits-grey-600">
                          {safeFormatDate(sub.submissionDate)}
                        </div>
                      </div>
                    </div>
                    {/* <div className="text-md font-medium text-bits-grey">
                      Checkpoints
                    </div> */}
                  </CardHeader>

                  <CardContent>
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value={`cp-${sub.checkPointId}`}>
                        <AccordionTrigger className="px-2 py-2">
                          <div className="flex items-center justify-between w-full pr-4">
                            <span className="font-medium text-sm truncate">
                              {sub.checkPointName}
                            </span>
                            {sub.gradeBreakdown?.length > 0 && (
                              <div className="flex items-center bg-gray-100 px-2 py-1 rounded text-xs font-semibold text-gray-800">
                                {sub.gradeBreakdown.reduce((acc, row) => acc + row.earnedPoints, 0)} / {sub.gradeBreakdown.reduce((acc, row) => acc + row.totalPoints, 0)}
                              </div>
                            )}
                          </div>
                        </AccordionTrigger>

                        <AccordionContent>
                          <CardContent className="space-y-3">
                            <div className="flex flex-col">
                              <span className="mt-1 text-sm font-medium text-bits-grey">
                                Maximum Attempts : {maxAttempts}
                              </span>
                              <span className="mt-1 text-sm font-medium text-bits-grey">
                                Attempts Left{" "}
                                <span className="pl-10 pr-1">:</span>
                                {maxAttempts !== undefined
                                  ? Math.max(0, maxAttempts - sub.attempts)
                                  : "N/A"}
                              </span>
                            </div>
                            <div className="text-md font-medium text-bits-grey">
                              Submitted Files
                            </div>
                            <Card className="p-3">
                              {sub.files?.length > 0 ? (
                                sub.files.map((f, fileIndex) => (
                                  <div
                                    key={fileIndex}
                                    className="flex flex-row items-center gap-2 w-full mb-2"
                                  >
                                    {getFileIcon(
                                      typeof f.name === "string" &&
                                        f.name.includes(".")
                                        ? f.name.substring(
                                            f.name.lastIndexOf(".") + 1,
                                          )
                                        : "unknown",
                                    )}
                                    <div className="flex flex-col text-sm font-medium">
                                      {f.name}
                                      <span className="text-xs font-normal text-bits-grey-600">
                                        {/* {f.size} */}
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => handleDownload(f)}
                                      className="ml-auto flex items-center rounded-md hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 cursor-pointer group"
                                      title={`Download ${f.name}`}
                                    >
                                      <Download className="h-5 w-5 text-bits-grey-600 group-hover:text-bits-blue transition-colors duration-200" />
                                    </button>
                                  </div>
                                ))
                              ) : (
                                <div className="text-sm text-bits-grey-600">
                                  No files submitted
                                </div>
                              )}
                            </Card>

                            {sub.instructor_feedback ? (
                              <div>
                                <div className="text-md font-medium text-bits-grey mb-1 mt-2">
                                  Feedback
                                </div>
                                <p className="bg-bits-blue-25 w-full text-sm font-normal text-bits-grey-600 p-4 rounded-md">
                                  {sub.instructor_feedback}
                                </p>
                              </div>
                            ) : null}

                            {sub.studentComments ? (
                              <div>
                                <div className="text-md font-medium text-bits-grey mb-1 mt-2">
                                  Student Comments
                                </div>
                                <p className="bg-bits-blue-25 w-full text-sm font-normal text-bits-grey-600 p-4 rounded-md">
                                  {sub.studentComments}
                                </p>
                              </div>
                            ) : null}

                            {sub.gradeBreakdown?.length > 0 && (
                              <>
                                <div className="text-md font-medium text-bits-grey">
                                  Grade Breakdown
                                </div>
                                <Card className="p-4">
                                  <div className="space-y-3">
                                    {sub.gradeBreakdown.map((row, rowIndex) => {
                                      return (
                                        <div
                                          key={rowIndex}
                                          className="flex items-center justify-between"
                                        >
                                          <span className="text-xs font-normal text-bits-grey-600">
                                            {row.criteriaName}
                                          </span>
                                          <span className="text-xs font-semibold text-bits-grey">
                                            {row.earnedPoints}/{row.totalPoints}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </Card>
                              </>
                            )}
                          </CardContent>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
